class InfiniteScroll extends HTMLElement {
  constructor() {
    super();

    this.handleSwatchClick = this.handleSwatchClick.bind(this);
    this.loadNextPage = this.loadNextPage.bind(this);
    this._onButtonClick = this.handleButtonClick.bind(this);
  }

  connectedCallback() {
    this.anchor = this.querySelector("a");
    if (!this.anchor) return;

    this.btn = this.querySelector(".infinite-scroll__button");
    if (this.btn) {
      this.btn.addEventListener("click", this._onButtonClick);
    }
  }

  disconnectedCallback() {
    if (this.btn) {
      this.btn.removeEventListener("click", this._onButtonClick);
    }
  }

  handleButtonClick(e) {
    e.preventDefault();
    this.loadNextPage();
  }

  async loadNextPage() {
    if (!this.anchor) return;

    this.anchor.style.display = "flex";
    this.anchor.innerText = "Loading...";

    const url = this.anchor.getAttribute("href");
    if (!url) {
      this.finishLoading();
      document.dispatchEvent(new CustomEvent("collection:infinite-scroll-end"));
      this.remove();
      return;
    }

    try {
      const response = await fetch(url);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, "text/html");

      const newGrid = html.querySelector("[data-product-grid]");
      const grid = document.querySelector("[data-product-grid]");
      let newCards = [];

      if (newGrid && grid) {
        newCards = Array.from(newGrid.children);
        newCards.forEach((child) => grid.appendChild(child));
        this.initQuickShop(newCards);
        this.initSwatches(newCards);
        document.dispatchEvent(new CustomEvent("collection:products-appended", { detail: { cards: newCards } }));
      }

      const newInfinite = html.querySelector("infinite-scroll");
      if (newInfinite) {
        this.replaceWith(newInfinite);

        const nextAnchor = newInfinite.querySelector("a");
        const nextUrl = nextAnchor?.getAttribute("href");
        if (!nextUrl) {
          document.dispatchEvent(new CustomEvent("collection:infinite-scroll-end"));
        }
      } else {
        document.dispatchEvent(new CustomEvent("collection:infinite-scroll-end"));
        this.remove();
      }
    } catch (err) {
      console.error("InfiniteScroll error:", err);
    }

    this.finishLoading();
  }

  finishLoading() {
    if (!this.anchor) return;
    this.anchor.style.display = "none";
    this.anchor.innerText = "";
  }

  initQuickShop(cards = []) {
    if (!cards || !cards.length) return;

    const buttons = cards.flatMap(card =>
      Array.from(card.querySelectorAll('[js-quick-view-button]'))
    );

    buttons.forEach((productBtn) => {
      if (productBtn.dataset.quickshopInit) return;
      productBtn.dataset.quickshopInit = "true";

      productBtn.addEventListener("click", (evt) => {
        evt.preventDefault();
        evt.stopPropagation();

        const el = productBtn.closest(".grid-product");
        if (!el) return;
        const productId = el.dataset.productId;
        const productHandle = el.dataset.productHandle;
        const btn = el.querySelector(".quick-product__btn");

        this.fetchProduct(productHandle, productId, btn);
      });

      const prefetchHandler = (evt) => {
        if (window.innerWidth <= 767) return;
        const el = productBtn.closest(".grid-product");
        const handle = el?.dataset?.productHandle;
        if (!handle) return;
        if (window.quickViewCache?.[handle]) return;
        const url = (Shopify?.routes?.root || "/") + "products/" + handle;
        fetch(url)
          .then((res) => res.text())
          .then((txt) => {
            window.quickViewCache = window.quickViewCache || {};
            window.quickViewCache[handle] = txt;
          })
          .catch(() => {});
        productBtn.removeEventListener("mouseover", prefetchHandler);
        productBtn.removeEventListener("focusin", prefetchHandler);
      };

      productBtn.addEventListener("mouseover", prefetchHandler);
      productBtn.addEventListener("focusin", prefetchHandler);
    });
  }

  initSwatches(cards = []) {
    if (!cards || !cards.length) return;

    const swatchWrappers = cards.flatMap(card =>
      Array.from(card.querySelectorAll(".color-swatch-wrapper"))
    );

    swatchWrappers.forEach((swatchWrapper) => {
      if (swatchWrapper.dataset.swatchInit) return;
      swatchWrapper.dataset.swatchInit = "true";

      swatchWrapper.addEventListener("click", (e) => this.handleSwatchClick(e));
    });
  }

  handleSwatchClick(event) {
    event.preventDefault();
    const target = event.target;
    if (!target) return;
    if (target.classList.contains("color-swatch")) {
      const productId = target.dataset.productId;
      const productSwatchesContainer = target.closest(`.grid-product__colors--${productId}`);
      if (productSwatchesContainer) {
        const allProductSwatches = productSwatchesContainer.querySelectorAll(".color-swatch-wrapper");
        allProductSwatches.forEach(swatch => swatch.classList.remove("selected"));
        const targetParent = target.closest(".color-swatch-wrapper");
        if (targetParent) targetParent.classList.add("selected");
      }

      const gridItem = target.closest(".grid__item");
      if (!gridItem) return;

      const targetImageSrc = target.dataset.variantImage;
      const mainImage = gridItem.querySelector('.grid__image-ratio image-element img, .image-wrap image-element img');
      if (mainImage && targetImageSrc) {
        mainImage.setAttribute("src", targetImageSrc);
        mainImage.setAttribute("srcset", targetImageSrc);
        gridItem.dataset.originalImage = targetImageSrc;
        gridItem.dataset.originalSrcset = targetImageSrc;
      }

      const targetURL = target.dataset.variantUrl;
      const mainURL = gridItem.querySelector(".grid-product__link");
      if (mainURL && targetURL) {
        mainURL.setAttribute("href", targetURL);
      }

      const colorValue = target.dataset.color;
      const allSizeSelectors = gridItem.querySelectorAll(".product__size-selector-value");
      if (allSizeSelectors.length > 0) {
        const colorScript = gridItem.querySelector(`script[data-color="${colorValue}"]`);
        if (colorScript) {
          let variants = [];
          try {
            variants = JSON.parse(colorScript.textContent);
          } catch (err) {
            variants = [];
            console.warn("Failed to parse variants for color:", colorValue, err);
          }

          allSizeSelectors.forEach(selector => {
            selector.classList.add("disabled");
            selector.removeAttribute("data-variant");
            selector.removeAttribute("js-variant-option-selector");
          });

          variants.forEach((variant, index) => {
            const variantID = variant.id;
            const available = variant.available;
            if (variantID && allSizeSelectors[index]) {
              allSizeSelectors[index].dataset.variant = variantID;
              if (available) {
                allSizeSelectors[index].classList.remove("disabled");
                allSizeSelectors[index].setAttribute("js-variant-option-selector", "");
              } else {
                allSizeSelectors[index].classList.add("disabled");
              }
            }
          });
        }
      }

      const isAvailable = !target.classList.contains("color-swatch--sold-out");
      const soldOutTag = gridItem.querySelector(".grid-product__tag--high-priority");
      const tagWrapper = gridItem.querySelector(".grid-product__tag_wrap");

      if (!isAvailable) {
        if (!soldOutTag && tagWrapper) {
          const newSoldOutTag = document.createElement("div");
          newSoldOutTag.className = "grid-product__tag grid-product__tag--high-priority sold--out";
          newSoldOutTag.textContent = (window.theme && window.theme.strings && window.theme.strings.soldOut) || "Sold out";
          tagWrapper.innerHTML = "";
          tagWrapper.appendChild(newSoldOutTag);
        }
      } else {
        if (soldOutTag && tagWrapper) {
          soldOutTag.remove();
          if (tagWrapper.children.length === 0 && gridItem.dataset.originalTags) {
            tagWrapper.innerHTML = gridItem.dataset.originalTags;
          }
        }
      }
    }
  }

  async fetchProduct(handle, productId, btn) {
    if (!handle) return;
    const holder = document.getElementById("QuickShopHolder-" + handle);
    let url = (Shopify?.routes?.root || "/") + "products/" + handle;
    url = url.replace("//", "/"); 

    const initializer = (text) => {
      const html = document.createElement("div");
      html.innerHTML = text;
      const div = html.querySelector("product-info");
      if (!div || !holder) return;

      div.dataset.updateUrl = "false";
      holder.innerHTML = "";
      holder.append(div);

      const quickViewModal = holder.closest(".modal.modal--quick-shop");
      if (!quickViewModal) return;

      if (quickViewModal.closest(".featured-products")) {
        const customQuickview = document.querySelector(".custom-quickview");
        if (customQuickview) {
          customQuickview.innerHTML = "";
          customQuickview.appendChild(quickViewModal);
          customQuickview.classList.remove("hidden");
        }
      }

      if (!quickViewModal.classList.contains("active")) quickViewModal.classList.add("active");

      const crossButton = quickViewModal.querySelector(".modal__close.js-modal-close");

      if (crossButton) {
        crossButton.addEventListener("click", () => {
          const modalContainer = holder.closest(`#QuickShopModal-${productId}`);
          if (modalContainer?.classList?.contains("active")) modalContainer.classList.remove("active");
          holder.innerHTML = "";
          const customQuickView = modalContainer?.closest(".custom-quickview");
          if (customQuickView) {
            customQuickView.classList.add("hidden");
            const section = customQuickView.closest(".shopify-section");
            const productCard = section?.querySelector(`.product__grid-item[data-product-id="${productId}"]`);
            if (productCard) productCard.appendChild(modalContainer);
          }
        });
      }

      const overlay = document.querySelector(".modal.modal--quick-shop.active .overlay");
      if (overlay) {
        overlay.addEventListener("click", () => {
          const quickShopModal = document.querySelector(".modal.modal--quick-shop.active");
          if (!quickShopModal) return;
          quickShopModal.classList.remove("active");
          const customQuickView = quickShopModal.closest(".custom-quickview");
          if (customQuickView) {
            customQuickView.classList.add("hidden");
            const section = customQuickView.closest(".shopify-section");
            const productCard = section?.querySelector(`.product__grid-item[data-product-id="${productId}"]`);
            if (productCard) productCard.appendChild(quickShopModal);
          }
        });
      }

      document.dispatchEvent(new CustomEvent("pdpMediaSlider:init"));

      if (btn) btn.classList.remove("quick-product__btn--not-ready");

      try {
        this.initPhotoSwipe(true);
      } catch (err) {
        console.warn("initPhotoSwipe failed:", err);
      }
    };

    if (window.quickViewCache?.[handle]) {
      initializer(window.quickViewCache[handle]);
    } else {
      fetch(url)
        .then((res) => res.text())
        .then((txt) => {
          window.quickViewCache = window.quickViewCache || {};
          window.quickViewCache[handle] = txt;
          initializer(txt);
        })
        .catch((err) => {
          console.error("fetchProduct error:", err);
        });
    }
  }

  initPhotoSwipe(quickView = false) {
    const buildSelector = (baseSelector) => (quickView ? `.modal--quick-shop ${baseSelector}` : baseSelector);

    const photoSwipeButtons = Array.from(document.querySelectorAll(buildSelector(".product__photo-zoom")));
    const pswpElement = document.querySelector(".pswp");
    const swiperContainers = Array.from(document.querySelectorAll(".swiper-container"));
    const QuickViewSlider = swiperContainers[1]?.swiper;

    const items = Array.from(document.querySelectorAll(buildSelector(".photoswipe__img")), (img) => ({
      msrc: img.currentSrc || img.src,
      src: img.getAttribute("data-photoswipe-src") || img.currentSrc || img.src,
      w: parseInt(img.getAttribute("data-photoswipe-width")) || img.naturalWidth || 800,
      h: parseInt(img.getAttribute("data-photoswipe-height")) || img.naturalHeight || 600,
      el: img,
    }));

    if (!pswpElement || !items.length) {
      return;
    }

    photoSwipeButtons.forEach((button) => {
      if (button.dataset.pswpInit) return;
      button.dataset.pswpInit = "true";
      button.addEventListener("click", (e) => handlePhotoZoom(e.currentTarget));
    });

    const handlePhotoZoom = (target) => {
      const targetImg = target.previousElementSibling;
      if (!targetImg) return;
      const index = Math.max(0, (parseInt(targetImg.getAttribute("data-index"), 10) || 1) - 1);

      const options = {
        index: index,
        bgOpacity: 1,
        showHideOpacity: true,
        allowPanToNext: true,
        captionEl: false,
        closeOnScroll: false,
        counterEl: true,
        history: false,
        pinchToClose: true,
        imageClickAction: "close",
        loop: true,
        preloaderEl: false,
        getThumbBoundsFn: function (idx) {
          var pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
          var thumbnail = items[idx].el;
          var rect = thumbnail.getBoundingClientRect();
          return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
        },
      };

      try {
        const gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
        gallery.init();

        if (QuickViewSlider) {
          gallery.listen("afterChange", function () {
            try {
              QuickViewSlider.slideTo(gallery.getCurrentIndex());
            } catch (err) {
            }
          });
        }
      } catch (err) {
        console.error("PhotoSwipe init error:", err);
      }
    };
  }
}

customElements.define("infinite-scroll", InfiniteScroll);
